import {
  Annotation,
  BBoxPixels,
  CategoryInfo,
  HiddenReason,
  KeypointAnnotation,
  UserVisibility,
} from "@/types/annotations";
import { ParsedKeypointAnnotation } from "./KeyPoints/types";
import { parseKeypointAnnotations } from "./KeyPoints/helpers";
import { isManualBBoxAnnotation } from "./Segmentation/helpers";
import { getMeasurementAxisValue, parseMeasurementEndpointName } from "@shared/types/measurementDescriptors";
import { buildMeasurementLineDrawSpecs } from "@/utils/annotationMeasurementUtils";

export type SegmentationModelVisibilityState = {
  id: string;
  userVisibility: UserVisibility;
};

export type SegmentationManualBBoxState = {
  id: string;
  bbox: BBoxPixels;
  category: CategoryInfo;
  userVisibility: UserVisibility;
  /**
   * Landmarks for a box the server has never seen. The edit channels are blind
   * to an unsaved id, so this is the only way a box drawn (and possibly moved)
   * before its first save can carry its geometry. Omitted once the box is
   * saved — from then on the stored landmarks are authoritative.
   */
  initialKeypoints?: KeypointAnnotation[];
};

export type SegmentationMeasurementEndpointState = {
  name: string;
  x: number;
  y: number;
};

export type SegmentationMeasurementAxisState = {
  key: string;
  start: SegmentationMeasurementEndpointState;
  end: SegmentationMeasurementEndpointState;
  /** null = the endpoints moved but nothing could value them; clear the axis. */
  valueM: number | null;
};

export type SegmentationMeasurementEditState = {
  id: string;
  axes: SegmentationMeasurementAxisState[];
};

export type SegmentationCategoryChangeState = {
  id: string;
  categoryName: string;
};

/**
 * Endpoint moves on a capture with no depth: positions only, because there is
 * nothing to recompute a physical value from. The server clears the affected
 * axis values, so every listed endpoint must be one the user actually touched.
 */
export type SegmentationKeypointPositionEditState = {
  id: string;
  endpoints: Array<{ name: string; x: number; y: number; preserveAxisValue?: true }>;
};

export type SegmentationAnnotationSaveState = {
  modelVisibilities: SegmentationModelVisibilityState[];
  manualBBoxAnnotations: SegmentationManualBBoxState[];
  measurementEdits: SegmentationMeasurementEditState[];
  keypointPositionEdits: SegmentationKeypointPositionEditState[];
  categoryChanges: SegmentationCategoryChangeState[];
};

const isPositiveFinite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

function buildMeasurementEdit(annotation: Annotation, saved?: Annotation): SegmentationMeasurementEditState | null {
  const adjustedAxisKeys = new Set(
    (annotation.userAdjustment?.keypointAdjustments ?? []).flatMap((adjustment) => {
      const parsed = parseMeasurementEndpointName(adjustment.name);
      return parsed ? [parsed.key] : [];
    })
  );

  // Provenance alone is not enough to decide what to send: a box moved without
  // any endpoint ever being hand-dragged carries no adjustments, and keying
  // only off them would move the bbox while silently stranding its landmarks.
  const savedKeypointByName = new Map(
    (saved?.keypoints ?? []).flatMap((keypoint) => (keypoint.name ? [[keypoint.name, keypoint] as const] : []))
  );
  const hasMovedEndpoint = (spec: {
    start: { name?: string; x: number; y: number };
    end: { name?: string; x: number; y: number };
  }) =>
    [spec.start, spec.end].some((keypoint) => {
      if (!keypoint.name) return false;
      const before = savedKeypointByName.get(keypoint.name);
      return Boolean(before) && (before!.x !== keypoint.x || before!.y !== keypoint.y);
    });

  // A drawn box is valued from depth without any endpoint moving — on release,
  // or later when depth arrives (possibly after a save that sent it bare) — so
  // provenance alone would leave the server copy without those values.
  const isManualBox = isManualBBoxAnnotation(annotation);

  const axes: SegmentationMeasurementAxisState[] = buildMeasurementLineDrawSpecs(annotation.keypoints).flatMap(
    (spec) => {
      if (!spec.start.name || !spec.end.name) return [];
      const stored = getMeasurementAxisValue(annotation.measurements, spec.key);
      const valueM = isPositiveFinite(stored) ? stored : null;
      const isFirstValue =
        isManualBox && valueM !== null && !isPositiveFinite(getMeasurementAxisValue(saved?.measurements, spec.key));
      if (!isFirstValue && !adjustedAxisKeys.has(spec.key) && !hasMovedEndpoint(spec)) return [];
      // An axis whose value is gone still has to travel: the endpoints moved,
      // and omitting it would leave the server with the old landmarks and the
      // old scalar while the bbox moved out from under them.
      return [
        {
          key: spec.key,
          start: { name: spec.start.name, x: spec.start.x, y: spec.start.y },
          end: { name: spec.end.name, x: spec.end.x, y: spec.end.y },
          valueM,
        },
      ];
    }
  );
  if (axes.length === 0) return null;

  return {
    id: annotation.id,
    axes,
  };
}

const isFiniteMeasurement = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** The delta by which an annotation's box moved, or null if it kept its place or changed size. */
export function boxTranslationDelta(current: Annotation, saved: Annotation): { dx: number; dy: number } | null {
  if (!current.bbox || !saved.bbox) return null;
  if (current.bbox.width !== saved.bbox.width || current.bbox.height !== saved.bbox.height) return null;

  const dx = current.bbox.x - saved.bbox.x;
  const dy = current.bbox.y - saved.bbox.y;
  return dx === 0 && dy === 0 ? null : { dx, dy };
}

/**
 * Whether ONE axis rode along with a rigid box move, judged per axis.
 *
 * Judging per annotation would mean a labeler who nudges a box and then adjusts
 * a single axis loses the values of every OTHER axis, which still translated
 * rigidly and whose geometry is therefore unchanged.
 */
export function isRigidlyTranslatedAxis(
  spec: { start: { name?: string; x: number; y: number }; end: { name?: string; x: number; y: number } },
  savedByName: Map<string, { x: number; y: number }>,
  delta: { dx: number; dy: number } | null
): boolean {
  if (!delta) return false;
  return [spec.start, spec.end].every((keypoint) => {
    if (!keypoint.name) return false;
    const before = savedByName.get(keypoint.name);
    if (!before) return false;
    return keypoint.x === before.x + delta.dx && keypoint.y === before.y + delta.dy;
  });
}

/**
 * Diff the measurement endpoints of annotations present in BOTH lists.
 *
 * An endpoint qualifies when it moved, or when its axis had a value that is now
 * gone — the second case covers a drag away and back before saving, where the
 * position matches again but the value still needs clearing server-side.
 */
function buildKeypointPositionEdits(
  annotations: Annotation[],
  savedAnnotations: Annotation[]
): SegmentationKeypointPositionEditState[] {
  const savedById = new Map(savedAnnotations.map((annotation) => [annotation.id, annotation]));

  return annotations.flatMap((annotation) => {
    const saved = savedById.get(annotation.id);
    if (!saved) return [];
    const translation = boxTranslationDelta(annotation, saved);

    const savedKeypointByName = new Map(
      (saved.keypoints ?? []).flatMap((keypoint) => (keypoint.name ? [[keypoint.name, keypoint] as const] : []))
    );
    // Only complete axis pairs are draggable/clearable, so enumerating the same
    // specs the renderer draws keeps the payload pair-shaped by construction.
    const endpoints = buildMeasurementLineDrawSpecs(annotation.keypoints).flatMap((spec) => {
      const rigid = isRigidlyTranslatedAxis(spec, savedKeypointByName, translation);
      return [spec.start, spec.end].flatMap((keypoint) => {
        if (!keypoint.name) return [];
        const savedKeypoint = savedKeypointByName.get(keypoint.name);
        const moved = !savedKeypoint || savedKeypoint.x !== keypoint.x || savedKeypoint.y !== keypoint.y;
        const valueCleared =
          isFiniteMeasurement(getMeasurementAxisValue(saved.measurements, spec.key)) &&
          !isFiniteMeasurement(getMeasurementAxisValue(annotation.measurements, spec.key));
        if (!moved && !valueCleared) return [];
        const endpoint: SegmentationKeypointPositionEditState["endpoints"][number] = {
          name: keypoint.name,
          x: keypoint.x,
          y: keypoint.y,
        };
        if (rigid) endpoint.preserveAxisValue = true;
        return [endpoint];
      });
    });

    return endpoints.length > 0 ? [{ id: annotation.id, endpoints }] : [];
  });
}

export type KeypointModelState = {
  id: string;
  visibility: number;
  /** Structured reason state distinguishes boundary/filter hides from explicit rejection. */
  userVisibility?: UserVisibility;
  /** Legacy/editor shorthand retained for the delta contract and older clients. */
  hiddenReason?: HiddenReason;
};

export type KeypointManualState = {
  id: string;
  x: number;
  y: number;
  visibility: number;
  category: string;
  keypointName?: string;
  hiddenReason?: HiddenReason;
  visibleVisibility?: number;
};

export type KeypointAnnotationSaveState = {
  modelKeypoints: KeypointModelState[];
  manualKeypoints: KeypointManualState[];
  deletedManualKeypointIds: string[];
};

export const KEYPOINT_ANNOTATION_MUTATION_BATCH_SIZE = 1000;

export function buildKeypointAnnotationMutationBatches(
  state: KeypointAnnotationSaveState
): KeypointAnnotationSaveState[] {
  const operationCount = Math.max(
    state.modelKeypoints.length,
    state.manualKeypoints.length,
    state.deletedManualKeypointIds.length
  );
  const batches: KeypointAnnotationSaveState[] = [];
  for (let offset = 0; offset < operationCount; offset += KEYPOINT_ANNOTATION_MUTATION_BATCH_SIZE) {
    batches.push({
      modelKeypoints: state.modelKeypoints.slice(offset, offset + KEYPOINT_ANNOTATION_MUTATION_BATCH_SIZE),
      manualKeypoints: state.manualKeypoints.slice(offset, offset + KEYPOINT_ANNOTATION_MUTATION_BATCH_SIZE),
      deletedManualKeypointIds: state.deletedManualKeypointIds.slice(
        offset,
        offset + KEYPOINT_ANNOTATION_MUTATION_BATCH_SIZE
      ),
    });
  }
  return batches;
}

/**
 * Resolve the schema name used for newly-created stand-count points in one pass.
 * A category schema is authoritative even when an earlier legacy annotation only
 * provides a name on its first point.
 */
export function resolveKeypointName(annotations: Annotation[]): string | undefined {
  let pointNameFallback: string | undefined;
  for (const annotation of annotations) {
    const schemaNames = annotation.category?.keypointNames;
    if (schemaNames !== undefined) {
      if (schemaNames.length === 1 && schemaNames[0]) return schemaNames[0];
      continue;
    }
    if (annotation.keypoints?.length === 1) {
      pointNameFallback ??= annotation.keypoints[0]?.name || undefined;
    }
  }
  return pointNameFallback;
}

/** Mirror the callable's visibility update in the local post-save snapshot. */
export function buildSavedKeypointUserVisibility(
  annotation: Annotation,
  modelKeypoint: KeypointModelState
): UserVisibility {
  if (modelKeypoint.userVisibility) return cloneUserVisibility(modelKeypoint.userVisibility);

  const keepsStoredSystemHiddenState =
    modelKeypoint.visibility === 0 &&
    annotation.userVisibility?.isVisible === false &&
    modelKeypoint.hiddenReason !== "confidenceBelowThreshold" &&
    modelKeypoint.hiddenReason !== "userToggledOff";
  if (keepsStoredSystemHiddenState) {
    return cloneUserVisibility(annotation.userVisibility);
  }
  if (modelKeypoint.visibility !== 0 && annotation.userVisibility?.isVisible === false) {
    return { isVisible: true, manualOverride: true };
  }
  const previousVisibility = annotation.keypoints?.[0]?.visibility;
  const replacesEditorHiddenReason =
    modelKeypoint.visibility === 0 &&
    (modelKeypoint.hiddenReason === "confidenceBelowThreshold" || modelKeypoint.hiddenReason === "userToggledOff") &&
    (annotation.userVisibility?.hiddenReasons?.length !== 1 ||
      annotation.userVisibility.hiddenReasons[0] !== modelKeypoint.hiddenReason);
  if (previousVisibility === modelKeypoint.visibility && !replacesEditorHiddenReason) {
    return annotation.userVisibility ?? { isVisible: modelKeypoint.visibility !== 0 };
  }
  return {
    isVisible: modelKeypoint.visibility !== 0,
    manualOverride: true,
    ...(modelKeypoint.visibility === 0
      ? { hiddenReasons: [modelKeypoint.hiddenReason ?? ("userToggledOff" as const)] }
      : {}),
  };
}

/** Apply the callable's model-point mutation to the local post-save metadata snapshot. */
export function applySavedModelKeypointState(annotation: Annotation, modelKeypoint: KeypointModelState): Annotation {
  if (modelKeypoint.userVisibility) {
    const keypoints = annotation.keypoints?.map((keypoint, index) =>
      index === 0 ? { ...keypoint, visibility: modelKeypoint.visibility } : keypoint
    );
    return {
      ...annotation,
      keypoints,
      ...(keypoints ? { numKeypoints: keypoints.filter(({ visibility }) => visibility > 0).length } : {}),
      userVisibility: cloneUserVisibility(modelKeypoint.userVisibility),
    };
  }

  const keepsStoredSystemHiddenState =
    modelKeypoint.visibility === 0 &&
    annotation.userVisibility?.isVisible === false &&
    modelKeypoint.hiddenReason !== "confidenceBelowThreshold" &&
    modelKeypoint.hiddenReason !== "userToggledOff";
  if (keepsStoredSystemHiddenState) return annotation;

  const preservesStoredVisibility =
    modelKeypoint.visibility === 0 && modelKeypoint.hiddenReason === "confidenceBelowThreshold";
  const keypoints = annotation.keypoints?.map((keypoint, index) =>
    index === 0 && !preservesStoredVisibility ? { ...keypoint, visibility: modelKeypoint.visibility } : keypoint
  );
  return {
    ...annotation,
    keypoints,
    ...(keypoints ? { numKeypoints: keypoints.filter(({ visibility }) => visibility > 0).length } : {}),
    userVisibility: buildSavedKeypointUserVisibility(annotation, modelKeypoint),
  };
}

/** Apply the callable's manual-point merge without dropping metadata the wire payload does not own. */
export function applySavedManualKeypointState(annotation: Annotation, manualKeypoint: KeypointManualState): Annotation {
  const tail = annotation.keypoints?.slice(1) ?? [];
  const ownsSinglePointSchema = tail.length === 0;
  const firstName =
    annotation.keypoints?.[0]?.name ?? (ownsSinglePointSchema ? manualKeypoint.keypointName : undefined);
  const previousVisibility = annotation.keypoints?.[0]?.visibility;
  const storedVisibility =
    manualKeypoint.visibility === 0 && manualKeypoint.hiddenReason === "confidenceBelowThreshold"
      ? previousVisibility === 1 || previousVisibility === 2
        ? previousVisibility
        : (manualKeypoint.visibleVisibility ?? 2)
      : manualKeypoint.visibility;
  const incomingCategory: CategoryInfo = {
    name: manualKeypoint.category,
    ...(ownsSinglePointSchema && manualKeypoint.keypointName ? { keypointNames: [manualKeypoint.keypointName] } : {}),
  };
  const category: CategoryInfo = {
    ...incomingCategory,
    ...(annotation.category?.keypointNames ? { keypointNames: annotation.category.keypointNames } : {}),
    ...(annotation.category?.keypointSkeleton ? { keypointSkeleton: annotation.category.keypointSkeleton } : {}),
  };
  const keypoints: KeypointAnnotation[] = [
    {
      ...(firstName ? { name: firstName } : {}),
      x: manualKeypoint.x,
      y: manualKeypoint.y,
      visibility: storedVisibility,
    },
    ...tail,
  ];
  return {
    ...annotation,
    category,
    keypoints,
    numKeypoints: keypoints.filter(({ visibility }) => visibility > 0).length,
    userVisibility: {
      isVisible: manualKeypoint.visibility !== 0,
      manualOverride: true,
      ...(manualKeypoint.visibility === 0 && manualKeypoint.hiddenReason
        ? { hiddenReasons: [manualKeypoint.hiddenReason] }
        : {}),
    },
  };
}

function cloneUserVisibility(userVisibility?: UserVisibility): UserVisibility {
  return {
    isVisible: userVisibility?.isVisible !== false,
    ...(userVisibility?.manualOverride !== undefined ? { manualOverride: userVisibility.manualOverride } : {}),
    ...(userVisibility?.hiddenReasons?.length ? { hiddenReasons: [...userVisibility.hiddenReasons] } : {}),
  };
}

function resolveParsedKeypointUserVisibility(annotation: ParsedKeypointAnnotation): UserVisibility {
  if (annotation.userVisibility) return cloneUserVisibility(annotation.userVisibility);
  if (annotation.hiddenReason) {
    return {
      isVisible: false,
      manualOverride: true,
      hiddenReasons: [annotation.hiddenReason],
    };
  }
  return { isVisible: annotation.keypoint.visibility !== 0 };
}

function userVisibilitiesMatch(left?: UserVisibility, right?: UserVisibility): boolean {
  const normalizedLeft = cloneUserVisibility(left);
  const normalizedRight = cloneUserVisibility(right);
  if (
    normalizedLeft.isVisible !== normalizedRight.isVisible ||
    normalizedLeft.manualOverride !== normalizedRight.manualOverride
  ) {
    return false;
  }
  const leftReasons = [...(normalizedLeft.hiddenReasons ?? [])].sort();
  const rightReasons = [...(normalizedRight.hiddenReasons ?? [])].sort();
  return (
    leftReasons.length === rightReasons.length && leftReasons.every((reason, index) => reason === rightReasons[index])
  );
}

export function buildSegmentationAnnotationSaveState(
  annotations: Annotation[],
  savedAnnotations: Annotation[] = [],
  options: {
    /**
     * The capture has no depth, so measurement edits travel as bare endpoint
     * positions instead of values. The two channels are mutually exclusive.
     */
    geometryOnlyMeasurementEdits?: boolean;
  } = {}
): SegmentationAnnotationSaveState {
  const geometryOnly = options.geometryOnlyMeasurementEdits === true;
  const savedCategoryById = new Map(savedAnnotations.map((annotation) => [annotation.id, annotation.category?.name]));
  const savedById = new Map(savedAnnotations.map((annotation) => [annotation.id, annotation]));
  const savedIds = new Set(savedAnnotations.map(({ id }) => id));
  const buildManualBBoxStates = (candidateAnnotations: Annotation[], withInitialKeypoints = false) =>
    candidateAnnotations.flatMap((annotation) =>
      isManualBBoxAnnotation(annotation) && annotation.bbox
        ? [
            {
              id: annotation.id,
              bbox: annotation.bbox,
              category: annotation.category,
              userVisibility: cloneUserVisibility(annotation.userVisibility),
              // Create-only: an id the server already has owns its landmarks.
              ...(withInitialKeypoints && !savedIds.has(annotation.id) && annotation.keypoints?.length
                ? { initialKeypoints: annotation.keypoints }
                : {}),
            },
          ]
        : []
    );
  const manualBBoxAnnotations = buildManualBBoxStates(annotations, true);
  return {
    modelVisibilities: annotations
      .filter((annotation) => !isManualBBoxAnnotation(annotation))
      .map((annotation) => ({
        id: annotation.id,
        userVisibility: cloneUserVisibility(annotation.userVisibility),
      })),
    manualBBoxAnnotations,
    measurementEdits: geometryOnly
      ? []
      : annotations.flatMap((annotation) => {
          const edit = buildMeasurementEdit(annotation, savedById.get(annotation.id));
          return edit ? [edit] : [];
        }),
    keypointPositionEdits: geometryOnly ? buildKeypointPositionEdits(annotations, savedAnnotations) : [],
    categoryChanges: annotations.flatMap((annotation) => {
      if (isManualBBoxAnnotation(annotation)) return [];
      const savedCategoryName = savedCategoryById.get(annotation.id);
      const categoryName = annotation.category?.name;
      return savedCategoryName !== undefined && categoryName && categoryName !== savedCategoryName
        ? [{ id: annotation.id, categoryName }]
        : [];
    }),
  };
}

function isStandaloneManualKeypointAnnotation(annotation: Annotation): boolean {
  return Boolean(
    annotation.keypoints?.length === 1 &&
      !annotation.bbox &&
      !annotation.segmentation &&
      "humanAnnotated" in annotation.source
  );
}

export function buildKeypointAnnotationSaveState(
  annotations: ParsedKeypointAnnotation[],
  category: string,
  keypointName: string | undefined,
  savedAnnotations: Annotation[] = []
): KeypointAnnotationSaveState {
  const savedModelStateById = new Map(
    parseKeypointAnnotations(savedAnnotations)
      .filter((annotation) => annotation.source === "model")
      .map((annotation) => [annotation.id, annotation] as const)
  );
  const savedManualById = new Map(
    savedAnnotations
      .filter((annotation) => "humanAnnotated" in annotation.source)
      .map((annotation) => [annotation.id, annotation] as const)
  );
  const savedParsedManualById = new Map(
    parseKeypointAnnotations(savedAnnotations)
      .filter((annotation) => annotation.source === "user")
      .map((annotation) => [annotation.id, annotation] as const)
  );
  const savedStandaloneManualIds = new Set(
    parseKeypointAnnotations(savedAnnotations.filter(isStandaloneManualKeypointAnnotation))
      .filter((annotation) => annotation.source === "user")
      .map(({ id }) => id)
  );
  const currentManualIds = new Set(
    annotations.filter((annotation) => annotation.source === "user").map(({ id }) => id)
  );
  return {
    modelKeypoints: annotations
      .filter((annotation) => annotation.source === "model")
      .filter((annotation) => {
        const saved = savedModelStateById.get(annotation.id);
        return (
          !saved ||
          saved.keypoint.visibility !== annotation.keypoint.visibility ||
          saved.hiddenReason !== annotation.hiddenReason ||
          saved.visibleVisibility !== annotation.visibleVisibility ||
          !userVisibilitiesMatch(saved.userVisibility, resolveParsedKeypointUserVisibility(annotation))
        );
      })
      .map((annotation) => ({
        id: annotation.id,
        visibility: annotation.keypoint.visibility,
        userVisibility: resolveParsedKeypointUserVisibility(annotation),
        ...(annotation.keypoint.visibility === 0 && annotation.hiddenReason
          ? { hiddenReason: annotation.hiddenReason }
          : {}),
      })),
    manualKeypoints: annotations
      .filter((annotation) => annotation.source === "user")
      .map((annotation) => ({
        id: annotation.id,
        x: annotation.keypoint.x,
        y: annotation.keypoint.y,
        visibility: annotation.keypoint.visibility,
        category,
        ...(keypointName ? { keypointName } : {}),
        ...(annotation.keypoint.visibility === 0 && annotation.hiddenReason
          ? { hiddenReason: annotation.hiddenReason }
          : {}),
        ...(annotation.keypoint.visibility === 0 && annotation.visibleVisibility !== undefined
          ? { visibleVisibility: annotation.visibleVisibility }
          : {}),
      }))
      .filter((manualKeypoint) => {
        const saved = savedManualById.get(manualKeypoint.id);
        const savedParsed = savedParsedManualById.get(manualKeypoint.id);
        const savedKeypoint = savedParsed?.keypoint;
        if (!saved || !savedParsed || !savedKeypoint) return true;
        return (
          savedKeypoint.x !== manualKeypoint.x ||
          savedKeypoint.y !== manualKeypoint.y ||
          savedKeypoint.visibility !== manualKeypoint.visibility ||
          savedParsed.hiddenReason !== manualKeypoint.hiddenReason ||
          savedParsed.visibleVisibility !== manualKeypoint.visibleVisibility ||
          saved.category?.name !== manualKeypoint.category ||
          (manualKeypoint.keypointName !== undefined &&
            (!savedKeypoint.name || saved.category?.keypointNames === undefined))
        );
      }),
    // The keypoint editor owns standalone points only. Human measurement boxes
    // can also carry keypoints, but omitting one from this editor must never
    // turn into a request to delete its bbox, landmarks, or measurements.
    deletedManualKeypointIds: [...savedStandaloneManualIds].filter((id) => !currentManualIds.has(id)),
  };
}
